// Libs
import { ReactNode, useState } from 'react';
import { Navigate } from 'react-router';

// Components
import Header from '~fpu/shared/components/navigation/Header';
import MemberDashboardNavLinks from './components/MemberDashboardNavLinks';
import SideNavigation from '~fpu/shared/components/navigation/SideNavigation';
import PageFooter from '~fpu/shared/components/PageFooter';
import AllAccessUpgradeDialog from './components/AllAccessUpgradeDialog';
import { PageAnalyticsWrapper } from '~fpu/shared/utils/PageAnalyticsWrapper';

// Hooks
import { useZendeskWidget } from '~fpu/shared/hooks/useZendeskWidget';
import { useHasFpuAllAccess } from '~fpu/shared/hooks/useHasFpuAllAccess';
import { useUserQuery } from '~fpu/shared/global-queries';

// Utils
import { PATHS } from '~fpu/config/constants';
import { analyticsLogger } from '~fpu/shared/utils/analytics';

function MemberDashboardLayout({
  children,
  page,
  allAccess = false
}: {
  children: ReactNode;
  page: string;
  allAccess?: boolean;
}) {
  useZendeskWidget('64039324-6f1f-4b4f-9067-3f8b3eaa7bac');

  const { data: hasFpuAllAccess } = useHasFpuAllAccess();

  const { data: user } = useUserQuery();
  const hasFpuAccess = user?.claims?.hasFpuAccess || false;

  if (allAccess && !hasFpuAccess) {
    return <Navigate to={PATHS.MEMBER_DASHBOARD.BASE} />;
  }

  return (
    <PageAnalyticsWrapper category="Member Dashboard" page={page}>
      <div className="flex min-h-screen flex-col">
        <Header
          link={PATHS.MEMBER_DASHBOARD.BASE}
          withAppSwitcher
          withNavigation
        />

        <SideNavigation>
          <MemberDashboardNavLinks />
        </SideNavigation>

        <div className="flex flex-1 flex-col">
          <div className="grow px-2 pt-3 pb-10 md:px-5 md:pt-4 lg:ml-32 lg:px-8">
            <div className="mx-auto max-w-screen-lg md:p-4 lg:p-0">
              {allAccess && !hasFpuAllAccess ? (
                <FpuAllAccessContent>{children}</FpuAllAccessContent>
              ) : (
                children
              )}
            </div>
          </div>
          <PageFooter />
        </div>
      </div>
    </PageAnalyticsWrapper>
  );
}

function FpuAllAccessContent({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const isOrHasRestrictedAnchorAncestor = (element: HTMLElement | null) => {
    if (!element) return false;

    if (element.tagName === 'A' && element.dataset.restricted === 'true') {
      analyticsLogger.trackElementClicked({
        element: 'FPU All Access Restricted Link',
        label: element.dataset.title
      });

      return true;
    }

    return isOrHasRestrictedAnchorAncestor(element.parentElement);
  };

  return (
    <>
      <div
        onClickCapture={(e) => {
          if (isOrHasRestrictedAnchorAncestor(e.target as HTMLElement)) {
            e.preventDefault();
            e.stopPropagation();

            setIsOpen(true);
          }
        }}
      >
        {children}
      </div>
      <AllAccessUpgradeDialog isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
}

export default MemberDashboardLayout;
